import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Wand2, Plus, Trash2, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sel = 'bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-2 py-1.5 text-[12.5px] outline-none focus:border-neutral-400';

const Bands = ({ bands, options, onChange, testPrefix }) => {
  const set = (i, patch) => onChange(bands.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  return (
    <div className="space-y-1.5">
      {bands.map((b, i) => (
        <div key={i} data-testid={`${testPrefix}-band-${i}`} className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-neutral-500 w-[118px]">{i === 0 ? 'Any order amount' : 'Orders from $'}</span>
          {i > 0 && <input data-testid={`${testPrefix}-min-${i}`} type="number" min="0" step="1" value={b.min_subtotal}
            onChange={(e) => set(i, { min_subtotal: e.target.value })} className={sel + ' w-24'} />}
          <span className="text-neutral-400">→</span>
          <select data-testid={`${testPrefix}-option-${i}`} value={b.option_id} onChange={(e) => set(i, { option_id: e.target.value })} className={sel}>
            <option value="">Choose shipping…</option>
            {options.map(o => <option key={o.id} value={o.id}>{o.label} (${Number(o.price).toFixed(2)})</option>)}
          </select>
          {i > 0 && <button type="button" data-testid={`${testPrefix}-remove-${i}`} onClick={() => onChange(bands.filter((_, j) => j !== i))} className="p-1 text-neutral-400 hover:text-red-500"><Trash2 size={13} /></button>}
        </div>
      ))}
      <button type="button" data-testid={`${testPrefix}-add-band`} onClick={() => onChange([...bands, { min_subtotal: '', option_id: '' }])}
        className="inline-flex items-center gap-1 text-[12px] text-brand font-semibold"><Plus size={12} /> Add order-size rule</button>
    </div>
  );
};

const toBands = (list) => (list?.length ? list.map(b => ({ ...b })) : [{ min_subtotal: 0, option_id: '' }]);
const clean = (bands) => bands.filter(b => b.option_id).map((b, i) => ({ option_id: b.option_id, min_subtotal: i === 0 ? 0 : Number(b.min_subtotal || 0) }));

export const AutoShippingRules = ({ headers, options, countries }) => {
  const [enabled, setEnabled] = useState(false);
  const [def, setDef] = useState(toBands());
  const [byCountry, setByCountry] = useState({});
  const [addCid, setAddCid] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/shipping-rules`, { headers }).then(({ data }) => {
      setEnabled(data.enabled); setDef(toBands(data.default_rules));
      setByCountry(Object.fromEntries(Object.entries(data.country_rules || {}).map(([k, v]) => [k, toBands(v)])));
    }).catch(() => setMsg('Failed to load rules'));
  }, [headers]);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await axios.put(`${API}/admin/shipping-rules`, {
        enabled, default_rules: clean(def),
        country_rules: Object.fromEntries(Object.entries(byCountry).map(([k, v]) => [k, clean(v)])),
      }, { headers });
      setMsg('Saved');
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };
  const unused = countries.filter(c => !byCountry[c.id]);

  return (
    <div data-testid="auto-shipping-rules" className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><Wand2 size={16} className="text-brand" /> Automatic shipping</h3>
          <p className="text-[12.5px] text-neutral-500 max-w-xl">When on, customers don't choose. Shipping is picked from their country and order amount (products subtotal in USD). Country prices and overrides below still apply.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-[13px] font-medium cursor-pointer">
          <input data-testid="auto-shipping-toggle" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
          {enabled ? 'On' : 'Off'}
        </label>
      </div>
      <div className="rounded-lg bg-neutral-50 dark:bg-neutral-900/50 p-3">
        <div className="text-[11px] tracking-widest uppercase text-neutral-500 mb-2">Default — all other countries</div>
        <Bands bands={def} options={options} onChange={setDef} testPrefix="rules-default" />
      </div>
      {Object.entries(byCountry).map(([cid, bands]) => {
        const c = countries.find(x => x.id === cid);
        return (
          <div key={cid} data-testid={`rules-country-${cid}`} className="rounded-lg bg-neutral-50 dark:bg-neutral-900/50 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] tracking-widest uppercase text-neutral-500">{c?.flag} {c?.name || cid}</div>
              <button type="button" data-testid={`rules-country-remove-${cid}`} onClick={() => setByCountry(({ [cid]: _, ...rest }) => rest)} className="text-[11.5px] text-neutral-500 hover:text-red-600">Remove country</button>
            </div>
            <Bands bands={bands} options={options} onChange={(v) => setByCountry(x => ({ ...x, [cid]: v }))} testPrefix={`rules-${cid}`} />
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="rules-add-country-select" value={addCid} onChange={(e) => setAddCid(e.target.value)} className={sel}>
          <option value="">Add a rule for a country…</option>
          {unused.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button type="button" data-testid="rules-add-country-btn" disabled={!addCid}
          onClick={() => { setByCountry(x => ({ ...x, [addCid]: toBands() })); setAddCid(''); }}
          className="rounded-md border border-neutral-300 dark:border-neutral-700 px-3 py-1.5 text-[12.5px] disabled:opacity-50">Add</button>
        <button type="button" data-testid="rules-save-btn" onClick={save} disabled={saving}
          className="ml-auto inline-flex items-center gap-1.5 btn-brand rounded-md px-4 py-2 text-[12.5px] font-semibold disabled:opacity-50"><Save size={13} /> {saving ? 'Saving…' : 'Save rules'}</button>
      </div>
      {msg && <div data-testid="rules-msg" className={`text-[12px] ${msg === 'Saved' ? 'text-emerald-600' : 'text-red-600'}`}>{msg}</div>}
    </div>
  );
};
